import { getIn } from 'formik'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import { Button, Card, CardBody, Col, Input, Row, TabContent, TabPane } from 'reactstrap'
import settings from '../../api/settings/hooks'
import { realEstateDescriptionFields, realEstateGeneralFields } from '../../common/forms/generalRealEstate/fields'
import { usePropertyDescriptionFormSelectOptions } from '../../pages/Properties/hooks'
import FormMainContainer from '../Common/FormMainContainer'
import FormSideContainer from '../Common/FormSideContainer'
import FormTabNavigation from '../Common/FormTabNavigation'
import RecursiveForm from '../RecursiveForm'
import RichTextEditor from '../RichTextEditor/RichTextEditor'
import PropertyFormDescriptionAI from './PropertyFormDescriptionAI'
import PropertyFormDescriptionContext from './PropertyFormDescriptionContext'

// TODO: ProjectFormDescriptionTab & PropertyFormDescriptionTab could be one reusable component
const PropertyFormDescriptionTab = (props) => {
    const { children, tabs, activeTab, handleTabChange, formik, inputs, subform, id } = props

    const currentLang = localStorage.getItem('i18nextLng')
    const propertyTypeId = getIn(formik?.values?.general, realEstateGeneralFields.PROPERTY_TYPE_ID)
    const propertyServiceId = getIn(formik?.values?.general, realEstateGeneralFields.PROPERTY_SERVICE_ID)

    const [activeTitleLanguage, setActiveTitleLanguage] = useState(currentLang)
    const [activeDescriptionLanguage, setActiveDescriptionLanguage] = useState(currentLang)

    const { data: availableTranslationLanguages = [] } = settings.GetApplicationSettings.useQuery({
        select: (data) => data.availableTranslationLanguages,
    })

    const { selectOptions, isPending: selectOptionsLoading } = usePropertyDescriptionFormSelectOptions(activeTab)

    const languageTabs = availableTranslationLanguages.map((lang) => ({
        id: lang.id,
        name: lang.abbreviation,
        label: lang.abbreviation,
        disabled: false,
    }))

    const languageSelectOptions = availableTranslationLanguages.map((lang) => ({
        value: lang.abbreviation,
        label: lang.abbreviation,
    }))

    const languageTranslateValues = availableTranslationLanguages.reduce((acc, curr) => {
        acc[curr.abbreviation] = ''
        return acc
    }, {})

    return (
        <PropertyFormDescriptionContext.Provider
            value={{
                tabs,
                activeTab,
                handleTabChange,
                formik,
                inputs,
                subform,
                selectOptions,
                selectOptionsLoading,
                id,
                activeTitleLanguage,
                setActiveTitleLanguage,
                activeDescriptionLanguage,
                setActiveDescriptionLanguage,
                languageTabs,
                languageSelectOptions,
                languageTranslateValues,
                propertyTypeId,
                propertyServiceId,
            }}
        >
            <Row>{children}</Row>
        </PropertyFormDescriptionContext.Provider>
    )
}

export const PropertyFormDescriptionTabSide = () => {
    const {
        id,
        inputs,
        formik,
        subform,
        selectOptions,
        selectOptionsLoading,
        activeTitleLanguage,
        setActiveTitleLanguage,
        languageTabs,
        languageTranslateValues,
        languageSelectOptions,
        propertyTypeId,
        propertyServiceId,
    } = useContext(PropertyFormDescriptionContext)

    const [useGoogleTranslate, setUseGoogleTranslate] = useState(false)
    const [activeGoogleTab, setActiveGoogleTab] = useState(activeTitleLanguage)
    const [googleTabs, setGoogleTabs] = useState(languageTabs)
    const [selectedGoogleLanguage, setSelectedGoogleLanguage] = useState()
    const [propertyTitleTranslateValues, setPropertyTitleTranslateValues] = useState(languageTranslateValues)
    const [googleLanguages, setGoogleLanguages] = useState(languageSelectOptions)

    useEffect(() => {
        if (languageTabs?.length) {
            setGoogleTabs(languageTabs)
        }
    }, [languageTabs])

    useEffect(() => {
        if (languageSelectOptions?.length) {
            setGoogleLanguages(languageSelectOptions)
        }
    }, [languageSelectOptions])

    if (inputs.sideForm) {
        const propertyTitleIndex = inputs.sideForm?.findIndex((obj) => obj.name === realEstateDescriptionFields.PROPERTY_TITLE)
        if (propertyTitleIndex !== -1) {
            inputs.sideForm[propertyTitleIndex].tabs = languageTabs
            inputs.sideForm[propertyTitleIndex].activeTab = activeTitleLanguage
            inputs.sideForm[propertyTitleIndex].handleTabChange = (tab) => setActiveTitleLanguage(tab)
        }
    }

    const handleInputChange = (value, language) => {
        setPropertyTitleTranslateValues((prevState) => {
            return {
                ...prevState,
                [language]: value,
            }
        })
    }

    const handleAcceptTranslations = () => {
        const formvalues = getIn(formik.values?.[subform], realEstateDescriptionFields.PROPERTY_TITLE)
        const newValues = Object.assign({}, formvalues, propertyTitleTranslateValues)

        formik.setFieldValue(`${subform}.${[realEstateDescriptionFields.PROPERTY_TITLE]}`, newValues)
        setUseGoogleTranslate(false)
        setPropertyTitleTranslateValues(languageTranslateValues)
    }

    return (
        <FormSideContainer>
            <Card>
                {propertyServiceId && propertyTypeId && (
                    <CardBody>
                        {id && <PropertyFormDescriptionAI />}

                        <hr className="my-4" />

                        <RecursiveForm
                            inputs={inputs?.sideForm}
                            subform={subform}
                            formik={formik}
                            selectOptions={selectOptions}
                            selectOptionsLoading={selectOptionsLoading}
                        />

                        {/* TODO: future functionality */}
                        {useGoogleTranslate && (
                            <React.Fragment>
                                <Row>
                                    <Col sm="12">
                                        <Select
                                            onChange={(e) => {
                                                let filteredTabs = languageTabs.filter((x) => x.name !== e.value)
                                                let filteredValues = filteredTabs.reduce(
                                                    (dict, item) => ({
                                                        ...dict,
                                                        [item.name]: '',
                                                    }),
                                                    {}
                                                )
                                                setSelectedGoogleLanguage(e)
                                                setGoogleTabs(filteredTabs)
                                                setActiveGoogleTab(filteredTabs[0].name)
                                                setPropertyTitleTranslateValues(filteredValues)
                                            }}
                                            onBlur={() => {
                                                // formik.setFieldTouched(fieldName)
                                            }}
                                            options={googleLanguages ? googleLanguages : []}
                                            id="propertyTitleGoogleLanguage"
                                            name="propertyTitleGoogleLanguage"
                                            // defaultInputValue={formik.values[inputField.name] || ""}
                                            value={selectedGoogleLanguage ?? ''}
                                            placeholder="Odaberi jezik"
                                        />
                                    </Col>
                                    <Col sm="12">
                                        <Button color="edit" className="btn-label my-3" onClick={(e) => e.preventDefault}>
                                            {' '}
                                            <i className="mdi mdi-arrow-right label-icon align-middle fs-16 me-2"></i> Pošalji
                                        </Button>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col sm="12">
                                        <FormTabNavigation
                                            customClass="language-input-tabs"
                                            tabs={googleTabs}
                                            activeTab={activeGoogleTab}
                                            handleTabChange={(tab) => setActiveGoogleTab(tab)}
                                        />
                                        <TabContent activeTab={activeGoogleTab}>
                                            {googleTabs?.map((tab) => (
                                                <TabPane key={`propertyTitleGoogleTranslate-${tab.name}`} tabId={tab.name}>
                                                    <Input
                                                        className="tabbed-input"
                                                        type="textarea"
                                                        style={{ resize: 'none', height: '108px' }}
                                                        id={`propertyTitleTranslate.${tab.name}`}
                                                        disabled={!selectedGoogleLanguage}
                                                        name={`propertyTitleTranslate.${tab.name}`}
                                                        onChange={(e) => handleInputChange(e.target.value, tab.name)}
                                                        value={propertyTitleTranslateValues?.[tab.name] || ''}
                                                    />
                                                </TabPane>
                                            ))}
                                        </TabContent>
                                    </Col>
                                    <Col sm="12">
                                        <Button color="success" className="btn-label mt-3" onClick={() => handleAcceptTranslations()}>
                                            {' '}
                                            <i className="mdi mdi-check label-icon align-middle fs-16 me-2"></i> Prihvati
                                        </Button>
                                    </Col>
                                </Row>
                            </React.Fragment>
                        )}
                    </CardBody>
                )}
            </Card>
        </FormSideContainer>
    )
}

export const PropertyFormDescriptionTabMain = () => {
    const { t } = useTranslation()
    const {
        tabs,
        activeTab,
        handleTabChange,
        inputs,
        formik,
        subform,
        activeDescriptionLanguage,
        setActiveDescriptionLanguage,
        languageTabs,
        languageTranslateValues,
        languageSelectOptions,
        propertyTypeId,
        propertyServiceId,
    } = useContext(PropertyFormDescriptionContext)

    const [useGoogleTranslate, setUseGoogleTranslate] = useState(false)
    const [activeGoogleTab, setActiveGoogleTab] = useState(activeDescriptionLanguage)
    const [googleTabs, setGoogleTabs] = useState(languageTabs)
    const [selectedGoogleLanguage, setSelectedGoogleLanguage] = useState()
    const [propertyDescriptionTranslateValues, setPropertyDescriptionTranslateValues] = useState(languageTranslateValues)
    const [googleLanguages, setGoogleLanguages] = useState(languageSelectOptions)

    useEffect(() => {
        if (languageTabs?.length) {
            setGoogleTabs(languageTabs)
        }
    }, [languageTabs])

    useEffect(() => {
        if (languageSelectOptions?.length) {
            setGoogleLanguages(languageSelectOptions)
        }
    }, [languageSelectOptions])

    if (inputs.mainForm) {
        const propertyDescriptionIndex = inputs.mainForm?.findIndex((obj) => obj.name === realEstateDescriptionFields.PROPERTY_DESCRIPTION)

        if (propertyDescriptionIndex !== -1) {
            inputs.mainForm[propertyDescriptionIndex].tabs = languageTabs
            inputs.mainForm[propertyDescriptionIndex].activeTab = activeDescriptionLanguage
            inputs.mainForm[propertyDescriptionIndex].handleTabChange = (tab) => setActiveDescriptionLanguage(tab)
        }
    }

    const handleAcceptTranslations = () => {
        const formvalues = getIn(formik.values?.[subform], realEstateDescriptionFields.PROPERTY_DESCRIPTION)
        const newValues = Object.assign({}, formvalues, propertyDescriptionTranslateValues)

        formik.setFieldValue(`${subform}.${[realEstateDescriptionFields.PROPERTY_DESCRIPTION]}`, newValues)
        setUseGoogleTranslate(false)
        setPropertyDescriptionTranslateValues(languageTranslateValues)
    }

    return (
        <FormMainContainer ainContainer tabs={tabs} activeTab={activeTab} handleTabChange={handleTabChange}>
            {propertyServiceId && propertyTypeId ? (
                <RecursiveForm inputs={inputs?.mainForm} subform={subform} formik={formik} />
            ) : (
                <Row>
                    <Col>
                        <p className="text-center">{t('form.property.description.message')}</p>
                    </Col>
                </Row>
            )}

            {/* TODO: future functionality */}
            {useGoogleTranslate && (
                <React.Fragment>
                    <Row>
                        <Col sm="12">
                            <Select
                                onChange={(e) => {
                                    // formik.setFieldValue(fieldName, e.value)
                                    let filteredTabs = languageTabs.filter((x) => x.name !== e.value)
                                    let filteredValues = filteredTabs.reduce(
                                        (dict, item) => ({
                                            ...dict,
                                            [item.name]: '',
                                        }),
                                        {}
                                    )
                                    setSelectedGoogleLanguage(e)
                                    setGoogleTabs(filteredTabs)
                                    setActiveGoogleTab(filteredTabs[0].name)
                                    setPropertyDescriptionTranslateValues(filteredValues)
                                }}
                                onBlur={() => {
                                    // formik.setFieldTouched(fieldName)
                                }}
                                options={googleLanguages ? googleLanguages : []}
                                id="propertyTitleGoogleLanguage"
                                name="propertyTitleGoogleLanguage"
                                // defaultInputValue={formik.values[inputField.name] || ""}
                                value={selectedGoogleLanguage ?? ''}
                                placeholder="Odaberi jezik"
                            />
                        </Col>
                        <Col sm="12">
                            <Button color="edit" className="btn-label my-3" onClick={(e) => e.preventDefault}>
                                {' '}
                                <i className="mdi mdi-arrow-right label-icon align-middle fs-16 me-2"></i> Pošalji
                            </Button>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm="12">
                            <FormTabNavigation
                                customClass="language-input-tabs"
                                tabs={googleTabs}
                                activeTab={activeGoogleTab}
                                handleTabChange={(tab) => setActiveGoogleTab(tab)}
                            />
                            <TabContent activeTab={activeGoogleTab}>
                                {googleTabs?.map((tab) => (
                                    <TabPane key={`propertyTitleGoogleTranslate-${tab.name}`} tabId={tab.name}>
                                        <RichTextEditor
                                            content={propertyDescriptionTranslateValues?.[tab.name] || ''}
                                            disabled={!selectedGoogleLanguage}
                                        />
                                    </TabPane>
                                ))}
                            </TabContent>
                        </Col>
                        <Col sm="12">
                            <Button color="success" className="btn-label mt-3" onClick={() => handleAcceptTranslations()}>
                                {' '}
                                <i className="mdi mdi-check label-icon align-middle fs-16 me-2"></i> Prihvati
                            </Button>
                        </Col>
                    </Row>
                </React.Fragment>
            )}
        </FormMainContainer>
    )
}

export default PropertyFormDescriptionTab
